/**
 * SupersededStickerDialog (2026-07-08)
 *
 * Blocking, AMBER station dialog shown when an operator scans a sticker that a
 * re-batch has invalidated (garment status SUPERSEDED). The shirt is fine — the
 * sticker is out of date. Unlike the red redo "discard" modal, this is
 * recoverable: the shirt gets a NEW sticker stuck over the old one.
 *
 * Two shapes, driven by the resolved replacement:
 *  - Has replacement → one-tap "Print new sticker" (reuses the GarmentLabel +
 *    usePrintLabels machinery — same as ReprintLabelDialog — then logs a
 *    reason:'superseded' reprint event). No reason picker.
 *  - No replacement (order reset but not yet re-batched) → "give it to your
 *    supervisor to set up again".
 *
 * PRODUCTION_DESIGN.md compliance: modal-level + audio (warning buzz fired by
 * useScanFeedback.triggerSuperseded), amber = warning/recoverable, large hit
 * targets (h-14 print CTA), operator-critical copy at text-lg+. Printing goes
 * through window.print (via usePrintLabels' iframe), NOT a stacked Radix layer
 * (§6.1) — so we print directly from this dialog.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ReplacementGarmentStatus, WorkflowStage } from '@threadheads/shared';
import { AlertTriangle, Printer, CheckCircle2, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { GarmentLabel } from '@/features/production-orders/components/labels/GarmentLabel';
import { usePrintLabels } from '@/features/production-orders/hooks/usePrintLabels';
import { useDefaultLabelSize } from '@/features/production-orders/hooks/useLabelTemplate';

/** Backend WorkflowStage → i18n key (Thread Lab operators read zh). */
const STAGE_I18N_KEY: Record<WorkflowStage, string> = {
  'Awaiting Batch': 'scanFeedback.stage.awaitingBatch',
  'In Batch': 'scanFeedback.stage.inBatch',
  Picking: 'scanFeedback.stage.picking',
  Printing: 'scanFeedback.stage.printing',
  Catching: 'scanFeedback.stage.catching',
  'Ready to Pack': 'scanFeedback.stage.readyToPack',
  Unknown: 'scanFeedback.stage.unknown',
};

function translateStage(t: TFunction, stage: string): string {
  const key = STAGE_I18N_KEY[stage as WorkflowStage];
  return key ? t(key) : stage;
}

export interface SupersededStickerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Order number for context, when the station knows it. Line omitted if null. */
  orderNumber: string | null;
  /** Live replacement, or null when the order was reset but not yet re-batched. */
  replacement: ReplacementGarmentStatus | null;
  /** Station location — forwarded to getGarmentLabel so cross-org 3PL replacements resolve. */
  locationId: string | null;
  /** This station's workflow stage — drives "scan again" vs "take it to {{stage}}". */
  currentStage?: WorkflowStage;
}

export function SupersededStickerDialog({
  open,
  onClose,
  orderNumber,
  replacement,
  locationId,
  currentStage,
}: SupersededStickerDialogProps) {
  const { t } = useTranslation('production');
  const { defaultLabelSize } = useDefaultLabelSize();
  const dismissRef = useRef<HTMLButtonElement>(null);

  const [hasTriggeredPrint, setHasTriggeredPrint] = useState(false);
  const [printed, setPrinted] = useState(false);

  // Pre-fetch the replacement's label as soon as the dialog opens so the print
  // tap is instant. Disabled (never fires) when there is no replacement.
  const { data: labelData, isLoading: labelLoading } =
    trpc.productionOrders.getGarmentLabel.useQuery(
      {
        garmentUnitId: replacement?.replacementId ?? '',
        locationId: locationId || undefined,
      },
      { enabled: open && !!replacement?.replacementId },
    );

  const logPrintEvent = trpc.productionOrders.logPrintEvent.useMutation();

  const { printRef, printLabels, isPrinting } = usePrintLabels({
    showSuccessToast: false,
    onPrintComplete: () => {
      setHasTriggeredPrint(false);
      if (!replacement) return;
      // One-tap reprint of the live sticker — keep the audit trail, no reason picker.
      logPrintEvent.mutate({
        garmentUnitId: replacement.replacementId,
        printType: 'LABEL_REPRINT',
        reason: 'superseded',
      });
      setPrinted(true);
    },
  });

  // Reset transient print state whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setHasTriggeredPrint(false);
      setPrinted(false);
    }
  }, [open]);

  // Focus the dismiss button on open (matches ScanErrorModal ergonomics) so a
  // stray scanner Enter dismisses rather than firing an accidental print.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => dismissRef.current?.focus(), 10);
    return () => clearTimeout(timer);
  }, [open]);

  // Fire the print once the label data has rendered into the hidden div.
  useEffect(() => {
    if (!hasTriggeredPrint || !labelData || !printRef.current) return;
    const timer = setTimeout(() => {
      printLabels('', defaultLabelSize, `Superseded-Reprint-${labelData.sku}`);
    }, 100);
    return () => clearTimeout(timer);
  }, [hasTriggeredPrint, labelData, printLabels, defaultLabelSize, printRef]);

  const handlePrint = () => {
    if (!labelData) return;
    setHasTriggeredPrint(true);
  };

  const isWorking = isPrinting || hasTriggeredPrint || logPrintEvent.isPending;

  const hasReplacement = !!replacement;
  const stageLabel = replacement ? translateStage(t, replacement.stage) : '';
  const sameStage = !!currentStage && replacement?.stage === currentStage;
  // Soften an Unknown stage: omit the "New sticker: …" line unless we have a name.
  const showNewStickerLine =
    hasReplacement && (!!replacement?.batchName || replacement?.stage !== 'Unknown');

  return (
    <>
      <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
        <AlertDialogContent className="max-w-lg sm:max-w-xl">
          <AlertDialogHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <AlertTriangle
                className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 text-amber-600 dark:text-amber-400"
                aria-hidden="true"
              />
              <AlertDialogTitle className="text-2xl sm:text-3xl font-bold text-amber-700 dark:text-amber-400">
                <span className="sr-only">{t('scanFeedback.warning')}</span>
                {t('scanFeedback.supersededTitle')}
              </AlertDialogTitle>
            </div>

            {/* Description block — plain, reviewed voice. asChild + a <div> lets us
                stack lines while keeping Radix's aria-describedby (screen-reader) link. */}
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-lg sm:text-xl text-foreground">
                {orderNumber && (
                  <span className="block font-mono text-base sm:text-lg text-muted-foreground tabular-nums">
                    {t('scanFeedback.supersededOrder', { orderNumber })}
                  </span>
                )}
                <span className="block font-semibold">{t('scanFeedback.supersededShirtFine')}</span>

                {hasReplacement ? (
                  <>
                    <span className="block">{t('scanFeedback.supersededHasNew')}</span>
                    <span className="block">
                      {sameStage
                        ? t('scanFeedback.supersededInstructionScan')
                        : t('scanFeedback.supersededInstructionTakeTo', { stage: stageLabel })}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="block">{t('scanFeedback.supersededNotReady')}</span>
                    <span className="block font-semibold">{t('scanFeedback.supersededSeeSupervisor')}</span>
                  </>
                )}
              </div>
            </AlertDialogDescription>

            {/* New-sticker locator chip */}
            {showNewStickerLine && (
              <p className="text-base sm:text-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3 text-amber-900 dark:text-amber-100">
                {replacement?.batchName
                  ? t('scanFeedback.supersededNewSticker', {
                      batchName: replacement.batchName,
                      stage: stageLabel,
                    })
                  : t('scanFeedback.supersededNewStickerNoBatch', { stage: stageLabel })}
              </p>
            )}

            {/* Brief success state after a reprint. */}
            {printed && (
              <p
                className="flex items-center gap-2 text-base sm:text-lg font-semibold text-emerald-700 dark:text-emerald-400"
                role="status"
                aria-live="polite"
              >
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                {t('scanFeedback.supersededPrinted')}
              </p>
            )}
          </AlertDialogHeader>

          <AlertDialogFooter className="gap-2">
            <AlertDialogAction
              ref={dismissRef}
              onClick={onClose}
              className={cn(
                'h-14 text-lg px-8 w-full sm:w-auto min-w-[120px] font-semibold',
                hasReplacement
                  ? 'bg-muted text-foreground hover:bg-muted/80 focus-visible:ring-amber-600'
                  : 'bg-amber-600 hover:bg-amber-700 text-white focus-visible:ring-amber-600',
              )}
            >
              {hasReplacement ? t('scanFeedback.supersededDone') : t('scanFeedback.ok')}
            </AlertDialogAction>

            {hasReplacement && (
              <Button
                onClick={handlePrint}
                disabled={isWorking || labelLoading || !labelData}
                className="h-14 text-lg px-8 w-full sm:w-auto min-w-[120px] font-semibold gap-2 bg-amber-600 hover:bg-amber-700 text-white focus-visible:ring-amber-600"
              >
                {isWorking ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                ) : (
                  <Printer className="h-5 w-5" aria-hidden="true" />
                )}
                {isWorking
                  ? t('scanFeedback.supersededPrinting')
                  : printed
                    ? t('scanFeedback.supersededPrintAgain')
                    : t('scanFeedback.supersededPrint')}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hidden label render target — usePrintLabels reads innerHTML from this ref. */}
      <div ref={printRef} style={{ display: 'none' }}>
        {labelData && (
          <GarmentLabel
            garment={{
              id: labelData.id,
              orderNumber: labelData.orderNumber,
              sku: labelData.sku,
              shopifySku: labelData.shopifySku,
              productName: labelData.productName,
              size: labelData.size,
              color: labelData.color,
              requiresPretreat: labelData.requiresPretreat,
              fulfillmentType: labelData.fulfillmentType,
              itemNumber: labelData.itemNumber,
              totalItems: labelData.totalItems,
              shopifyProductType: labelData.shopifyProductType,
              printTechnology: labelData.printTechnology,
              // Phase 104 (D-09): manual order print areas ([] otherwise).
              printArea: labelData.printArea,
            }}
            binLabel={labelData.binLabel ?? undefined}
            isRedo={labelData.isRedo}
            redoReason={labelData.redoReason ?? undefined}
          />
        )}
      </div>
    </>
  );
}
